import type { ReactElement } from 'react'

import { Navigate, useSearchParams } from 'react-router-dom'
import { useIsMobile } from '../hooks/useIsMobile'
import { isAffiliate } from '../services/api/affiliates'
import { isFgtafo } from '../services/api/fgtafo'
import { updatePlatformHeader } from '../services/api/auth'
import { setPlatformForLogs } from '../services/datadog'
import { environmentVariables } from '../utils/environmentVariables'
import { redirectToUrl } from '../utils/location'
import { originParamUrl } from '../utils/originRedirect'
import { BaseSignInRoute } from './BaseSignInRoute'
import { useIsTouchDevice } from '../hooks/useIsTouchDevice'

type SignInContentProps = {
  searchParams: URLSearchParams
  platform: string
}

const SignInContent = ({ searchParams, platform }: SignInContentProps): ReactElement => {
  const redirectionAfterSignInUrl = async (accessToken: string): Promise<string> => {
    const originParamUrlValue = originParamUrl(searchParams)
    if (originParamUrlValue) {
      return originParamUrlValue
    }
    const userIsFgtafo = await isFgtafo(accessToken)
    if (userIsFgtafo) {
      return environmentVariables('SHOP_APP_URL')
    }
    const userIsAffiliate = await isAffiliate(accessToken)
    if (userIsAffiliate) {
      return environmentVariables('AFFILIATES_APP_URL')
    }
    return environmentVariables('EMPLOYEE_APP_URL')
  }

  return BaseSignInRoute(async (accessToken) => {
    redirectToUrl(await redirectionAfterSignInUrl(accessToken))
  }, platform)
}

export const SignInRoute = (): ReactElement => {
  const [searchParams] = useSearchParams()
  const isSmallScreen = useIsMobile()
  const isTouchDevice = useIsTouchDevice()
  const isMobile = isSmallScreen && isTouchDevice
  const bypassMobileRedirect = searchParams.get('bypass_mobile') === 'true'
  const platform = 'web'

  updatePlatformHeader(platform)
  setPlatformForLogs(platform)

  // All hooks called before conditional rendering to avoid hook violations
  return isMobile && !bypassMobileRedirect ? (
    <Navigate to={'/signin/mobile'} replace />
  ) : (
    <SignInContent searchParams={searchParams} platform={platform} />
  )
}
